export function angleToValue({ angle, minValue, maxValue, startAngle, endAngle }) {
  if (endAngle <= startAngle) throw new Error('endAngle must be greater than startAngle');
  if (angle < startAngle) return minValue;
  if (angle > endAngle) return maxValue;
  const ratio = (angle - startAngle) / (endAngle - startAngle);
  return ratio * (maxValue - minValue) + minValue;
}

export function valueToAngle({ value, minValue, maxValue, startAngle, endAngle }) {
  if (endAngle <= startAngle) throw new Error('endAngle must be greater than startAngle');
  const ratio = (value - minValue) / (maxValue - minValue);
  return ratio * (endAngle - startAngle) + startAngle;
}

function convertAngle(degree, from, to = { direction: 'ccw', axis: '+x' }) {
  if (from.direction !== to.direction) {
    degree = degree === 0 ? 0 : 360 - degree;
  }
  if (from.axis === to.axis) return degree;
  if (from.axis[1] === to.axis[1]) return (180 + degree) % 360;

  switch (to.direction + from.axis + to.axis) {
    case 'ccw+x-y': case 'ccw-x+y': case 'ccw+y+x': case 'ccw-y-x':
    case 'cw+y-x':  case 'cw-y+x':  case 'cw-x-y':  case 'cw+x+y':
      return (90 + degree) % 360;
    case 'ccw+y-x': case 'ccw-y+x': case 'ccw+x+y': case 'ccw-x-y':
    case 'cw+x-y':  case 'cw-x+y':  case 'cw+y+x':  case 'cw-y-x':
      return (270 + degree) % 360;
    default:
      throw new Error('Unhandled angle conversion');
  }
}

export function angleToPosition(angle, radius, svgSize) {
  const deg = convertAngle(angle.degree, angle, { direction: 'ccw', axis: '+x' });
  const rad = (deg / 180) * Math.PI;
  let dX, dY;

  if (rad <= Math.PI) {
    if (rad <= Math.PI / 2) {
      dY = Math.sin(rad) * radius;
      dX = Math.cos(rad) * radius;
    } else {
      dY = Math.sin(Math.PI - rad) * radius;
      dX = Math.cos(Math.PI - rad) * radius * -1;
    }
  } else {
    if (rad <= Math.PI * 1.5) {
      dY = Math.sin(rad - Math.PI) * radius * -1;
      dX = Math.cos(rad - Math.PI) * radius * -1;
    } else {
      dY = Math.sin(2 * Math.PI - rad) * radius * -1;
      dX = Math.cos(2 * Math.PI - rad) * radius;
    }
  }

  return { x: dX + svgSize / 2, y: svgSize / 2 - dY };
}

export function positionToAngle(position, svgSize, angleType) {
  const dX = position.x - svgSize / 2;
  const dY = svgSize / 2 - position.y;
  let theta = Math.atan2(dY, dX);
  if (theta < 0) theta += 2 * Math.PI;
  const degree = (theta / Math.PI) * 180;
  return convertAngle(degree, { direction: 'ccw', axis: '+x' }, angleType);
}

export function arcPathWithRoundedEnds({ startAngle, endAngle, angleType, innerRadius, thickness, svgSize, direction }) {
  if (startAngle % 360 === endAngle % 360 && startAngle !== endAngle) {
    endAngle = endAngle - 0.001;
  }

  const largeArc = endAngle - startAngle >= 180;
  const outerRadius = innerRadius + thickness;
  const cw  = direction === 'cw' ? '1' : '0';
  const ccw = direction === 'cw' ? '0' : '1';
  const arc = largeArc ? '1' : '0';
  const half = thickness / 2;

  const innerArcStart = angleToPosition({ degree: startAngle, ...angleType }, innerRadius, svgSize);
  const innerArcEnd   = angleToPosition({ degree: endAngle,   ...angleType }, innerRadius, svgSize);
  const outerArcStart = angleToPosition({ degree: endAngle,   ...angleType }, outerRadius, svgSize);
  const outerArcEnd   = angleToPosition({ degree: startAngle, ...angleType }, outerRadius, svgSize);

  return [
    `M ${innerArcStart.x},${innerArcStart.y}`,
    `A ${innerRadius} ${innerRadius} 0 ${arc} ${cw}  ${innerArcEnd.x}   ${innerArcEnd.y}`,
    `A ${half}        ${half}        0 ${arc} ${ccw} ${outerArcStart.x} ${outerArcStart.y}`,
    `A ${outerRadius} ${outerRadius} 0 ${arc} ${ccw} ${outerArcEnd.x}   ${outerArcEnd.y}`,
    `A ${half}        ${half}        0 ${arc} ${ccw} ${innerArcStart.x} ${innerArcStart.y}`,
    'Z',
  ].join(' ');
}
